// src/components/Profile.js
import React, { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase-config";
import { doc, updateDoc, getDocs, collection } from "firebase/firestore";
import Handicap from "./Handicap";
import ProfilePhotoUpload from "./ProfilePhotoUpload";
import BagTag from "./BagTag"; // Import the BagTag component
import ProfileView from "./ProfileView";

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [regionsLoading, setRegionsLoading] = useState(true);

  // Profile fields managed locally for editing
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [location, setLocation] = useState("");
  // Handicap is stored here as a string but will be converted to a number on saving.
  const [handicap, setHandicap] = useState("");
  const [gender, setGender] = useState("");
  const [region, setRegion] = useState("");
  const [bagTag, setBagTag] = useState("");
  const [photoURL, setPhotoURL] = useState("");

  // Save the initial values to determine if changes were made.
  const [originalProfile, setOriginalProfile] = useState(null);

  // Local state for regions fetched from Firestore.
  const [regions, setRegions] = useState([]);

  // State for controlling the preview popup.
  const [previewOpen, setPreviewOpen] = useState(false);

  // Snackbar state for notifications.
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Initialize profile fields from the user object when it becomes available.
  useEffect(() => {
    if (user) {
      const fName = user.firstName || "";
      const lName = user.lastName || "";
      const loc = user.location || "";
      const gen = user.gender || "";
      const reg = user.region || "";
      const tag = user.bagTag || "";
      const photo = user.photoURL || "";
      // Ensure we convert handicap to a string for editing (if it exists).
      const hcap = user.handicap !== undefined && user.handicap !== null ? String(user.handicap) : "";
      setFirstName(fName);
      setLastName(lName);
      setLocation(loc);
      setHandicap(hcap);
      setGender(gen);
      setRegion(reg);
      setBagTag(tag);
      setPhotoURL(photo);
      setOriginalProfile({
        firstName: fName,
        lastName: lName,
        location: loc,
        gender: gen,
        region: reg,
        bagTag: tag,
        photoURL: photo,
        handicap: hcap,
      });
      setLoading(false);
    }
  }, [user]);

  // Fetch regions from Firestore.
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regionsSnapshot = await getDocs(collection(db, "regions"));
        const regionsList = regionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRegions(regionsList);
      } catch (err) {
        console.error("Error fetching regions:", err);
      } finally {
        setRegionsLoading(false);
      }
    };

    fetchRegions();
  }, []);

  // Helper function to check for changes.
  const isProfileDirty = () => {
    if (!originalProfile) return false;
    return (
      firstName !== originalProfile.firstName ||
      lastName !== originalProfile.lastName ||
      location !== originalProfile.location ||
      gender !== originalProfile.gender ||
      region !== originalProfile.region ||
      bagTag !== originalProfile.bagTag ||
      photoURL !== originalProfile.photoURL ||
      handicap !== originalProfile.handicap
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const docRef = doc(db, "users", user.uid);
      await updateDoc(docRef, {
        firstName: firstName || null,
        lastName: lastName || null,
        // Update the lower-case versions as well.
        firstNameLower: firstName ? firstName.toLowerCase() : null,
        lastNameLower: lastName ? lastName.toLowerCase() : null,
        location: location || null,
        gender: gender || null,
        region: region || null,
        photoURL: photoURL || null,
        bagTag: bagTag || null,
        // Convert handicap to a number if a value is provided.
        handicap: handicap.trim() === "" ? null : parseFloat(handicap),
      });
      setSnackbarMessage("Profile Updated Successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      // Update the original profile to the new values after save.
      setOriginalProfile({
        firstName,
        lastName,
        location,
        gender,
        region,
        bagTag,
        photoURL,
        handicap,
      });
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed to update profile. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Handler to close the snackbar.
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  if (loading || regionsLoading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      {/* Display the user's first name in the title if available */}
      <Typography variant="h4" gutterBottom>
        {firstName ? `${firstName}'s Profile` : "Your Profile"}
      </Typography>
      <Paper elevation={3} sx={{ p: 3, position: "relative" }}>
        {/* Preview Profile Button in the top right */}
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ position: "absolute", top: 8, right: 8 }}
          onClick={() => setPreviewOpen(true)}
        >
          Preview Profile
        </Button>
        <form onSubmit={handleSave}>
          {/* 1. Profile Photo Upload */}
          <ProfilePhotoUpload
            currentPhotoURL={photoURL}
            onUploadSuccess={(newURL) => setPhotoURL(newURL)}
          />
          {/* 2. Bag Tag Component */}
          <BagTag value={bagTag} onChange={setBagTag} />
          {/* 3. Email, Names, etc. */}
          <TextField
            label="Email"
            type="email"
            value={user.email}
            fullWidth
            margin="normal"
            disabled
          />
          <TextField
            label="First Name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Last Name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Gender"
            select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            fullWidth
            margin="normal"
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </TextField>
          <TextField
            label="Region"
            select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            fullWidth
            margin="normal"
          >
            {regions.map(({ id, regionName }) => (
              <MenuItem key={id} value={id}>
                {regionName}
              </MenuItem>
            ))}
          </TextField>
          {/* Handicap Section */}
          {user.isHandicapApproved ? (
            <TextField
              label="Official Handicap"
              type="number"
              value={handicap}
              fullWidth
              margin="normal"
              disabled
            />
          ) : (
            <TextField
              label="Unofficial Handicap"
              type="number"
              value={handicap}
              onChange={(e) => setHandicap(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                inputProps: {
                  style: { WebkitAppearance: "none", MozAppearance: "textfield" },
                },
              }}
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                  display: "none",
                },
              }}
            />
          )}
          {/* Always display the Handicap component */}
          <Handicap currentHandicap={handicap} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isProfileDirty()}
            >
              Save Profile
            </Button>
          </Box>
        </form>
      </Paper>
      {/* Preview Profile Dialog using ProfileView */}
      <ProfileView
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        userId={user.uid}
      />
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;